import React, { useState, useEffect, useRef } from 'react';

// Phone/tablet version of the step bar (shown below 992px, hidden on larger
// screens by CSS). It shows where you are in words, "Step 3 of 5 /
// Select languages", plus a 5-part progress line. "All steps" opens a
// list of every step by name: earlier steps can be tapped to go back
// (same goToStep / onStepBack as the desktop bar). Going forward is
// only through the step's own Continue button, which saves the step.
const StepNavMobile = ({ steps, currentStep, onStepBack, onStepSelect }) => {
    const [open, setOpen] = useState(false);
    const toggleRef = useRef(null);
    const total = steps.length;
    const current = steps[currentStep - 1] || steps[0];

    useEffect(() => {
        if (!open) return undefined;
        const onKey = event => {
            if (event.key === 'Escape') {
                setOpen(false);
                if (toggleRef.current) toggleRef.current.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // Close the list whenever the step changes.
    useEffect(() => {
        setOpen(false);
    }, [currentStep]);

    const select = index => {
        setOpen(false);
        if (onStepSelect) onStepSelect(index);
    };

    return (
        <div className="step-nav-mobile">
            <div className="snm-bar">
                {currentStep > 1 ? (
                    <button
                        type="button"
                        className="snm-back"
                        aria-label="Back to the previous step"
                        onClick={onStepBack}
                    >
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                    </button>
                ) : (
                    <span className="snm-back-space" aria-hidden="true" />
                )}

                <div className="snm-where" aria-live="polite">
                    <span className="snm-count">
                        Step {currentStep} of {total}
                    </span>
                    <span className="snm-name">{current.name}</span>
                </div>

                <button
                    type="button"
                    ref={toggleRef}
                    className="snm-toggle"
                    aria-expanded={open}
                    aria-controls="step-nav-mobile-list"
                    onClick={() => setOpen(!open)}
                >
                    All steps
                    <i
                        className={`fas fa-chevron-${open ? 'up' : 'down'}`}
                        aria-hidden="true"
                    />
                </button>
            </div>

            <div className="snm-progress" aria-hidden="true">
                {steps.map((step, ix) => (
                    <span
                        key={step.name}
                        className={
                            ix + 1 === currentStep
                                ? 'is-current'
                                : ix + 1 < currentStep
                                ? 'is-done'
                                : ''
                        }
                    />
                ))}
            </div>

            {!open && <ol id="step-nav-mobile-list" hidden />}
            {open && (
                <React.Fragment>
                    <div
                        className="snm-backdrop"
                        aria-hidden="true"
                        onClick={() => setOpen(false)}
                    />
                    <ol id="step-nav-mobile-list" className="snm-list">
                        {steps.map((step, ix) => {
                            const index = ix + 1;
                            const isCurrent = index === currentStep;
                            const reached = index < currentStep;
                            const canGo = !!onStepSelect && reached;
                            const status = isCurrent
                                ? 'You are here'
                                : reached
                                ? 'Done'
                                : 'Not yet';
                            const content = (
                                <React.Fragment>
                                    <span
                                        className="snm-num"
                                        aria-hidden="true"
                                    >
                                        {reached && !isCurrent ? (
                                            <i className="fas fa-check" />
                                        ) : (
                                            index
                                        )}
                                    </span>
                                    <span className="snm-item-text">
                                        <span className="snm-item-name">
                                            {step.name}
                                        </span>
                                        <span className="snm-item-status">
                                            {status}
                                        </span>
                                    </span>
                                    {canGo && (
                                        <i
                                            className="fas fa-chevron-right snm-go"
                                            aria-hidden="true"
                                        />
                                    )}
                                </React.Fragment>
                            );

                            return (
                                <li
                                    key={step.name}
                                    className={
                                        isCurrent
                                            ? 'is-current'
                                            : reached
                                            ? 'is-done'
                                            : 'is-later'
                                    }
                                >
                                    {canGo ? (
                                        <button
                                            type="button"
                                            className="snm-item"
                                            onClick={() => select(index)}
                                        >
                                            {content}
                                        </button>
                                    ) : (
                                        <div
                                            className="snm-item"
                                            aria-current={
                                                isCurrent ? 'step' : undefined
                                            }
                                        >
                                            {content}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </React.Fragment>
            )}
        </div>
    );
};

export default StepNavMobile;
